import {
  Button,
  Colors,
  Dialog,
  DialogBody,
  DialogFooter,
  EditableText,
  Icon,
} from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import {
  useDarkMode,
  Box,
  Text,
  TitleComponent,
  CustomSelect,
  CustomCard,
  ThemedDialog,
} from "ui-components";
import styled from "styled-components";
import { CompanyType } from "ui-io/company-io";
import { useEffect, useState } from "react";
import TagsMultiSelect from "../pages/configuration/create/company/tags";
import BadgesMultiSelect from "../pages/configuration/create/company/badges";
import {
  CATEGORY_OPTIONS_OBJ,
  INDUSTRY_OPTIONS_OBJ,
  MARKET_CAP_OPTIONS_OBJ,
} from "../utils/constants";
import openInNewTab from "../utils/open-in-new-tab";
import moment from "moment";
import Categories from "../pages/configuration/create/company/categories";

const CompanyDetailDisplay = ({
  companyJson,
  setCompanyJson,
}: {
  companyJson: CompanyType;
  setCompanyJson: React.Dispatch<React.SetStateAction<CompanyType>>;
}) => {
  const [darkMode] = useDarkMode();
  const [tagValues, setTagValues] = useState<string[]>(companyJson.tags ?? []);
  const [categoryValues, setCategoryValues] = useState<string[]>(
    companyJson.industry?.category as string[] ?? []
  );
  const [eyeOpen, setEyeOpen] = useState(false);
  const [badgeValues, setBadgeValues] = useState<(string|null)[]>(
    companyJson.badges ?? []
  );
  const industryCurrentObj = INDUSTRY_OPTIONS_OBJ.find(
    (ele) => ele.value === companyJson.industry?.industry
  );
  const categoryValue = Array.isArray(companyJson.industry?.category)
    ? companyJson.industry.category[0]
    : (companyJson.industry?.category ?? "");
  const categoryCurrentObj = CATEGORY_OPTIONS_OBJ.find(
    (ele) => ele.value === categoryValue
  );
  const marketCurrentObj = MARKET_CAP_OPTIONS_OBJ.find(
    (ele) => ele.value === companyJson.industry?.market_cap
  );
  const handleChange = (name: string, value: string | string[]) => {
    if (!companyJson) return;

    const updated: CompanyType = JSON.parse(JSON.stringify(companyJson));

    const path = name.split(".");
    let obj: any = updated;

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      obj[key] = obj[key] ?? {};
      obj = obj[key];
    }

    obj[path[path.length - 1]] = value;
    setCompanyJson(updated);
  };
  useEffect(() => {
    setTagValues(companyJson.tags ?? []);
    setBadgeValues(companyJson.badges ?? []);
    if (
      companyJson.industry?.category &&
      Array.isArray(companyJson.industry.category)
    )
      setCategoryValues(companyJson.industry?.category as string[]);
  }, [companyJson]);
  return (
    <Box flexDir="col" mx="large" mt="medium" gap="medium">
      <Box justify="start" gap="small" flexDir="row">
        <FlexColumn>
          <Box flexDir="row" items="center" gap={"small"}>
            <TitleComponent fontSize="15px" marginTop="0px">
              Company No.:
            </TitleComponent>
            <Text muted>{companyJson?.licence_no ?? ""}</Text>
          </Box>

          <Box flexDir="row" items="center" justify="start" gap="small">
            <TitleComponent fontSize="15px" marginTop="0px">
              Name:
            </TitleComponent>
            <Text muted>{companyJson?.name ?? ""}</Text>
          </Box>

          <Box flexDir="row" gap="medium" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              Website:
            </TitleComponent>
            <Box flexDir="row" items="center" gap={"xsmall"} width={"2/3"}>
              <EditableWrapper
                placeholder="Write something ..."
                value={companyJson?.url?.website ?? ""}
                onChange={(val) => handleChange("url.website", val)}
                maxLines={1}
                className={`${darkMode ? "muted-text" : "muted-text-opacity-1"} `}
              />
              <Tooltip2
                content="Open Company Website"
                className="align-center justify-center"
              >
                <Icon
                  className="cursor-pointer mb-xs"
                  color="#ABB3BF"
                  icon="share"
                  iconSize={13}
                  onClick={() => openInNewTab(companyJson?.url?.website || "")}
                />
              </Tooltip2>
            </Box>
          </Box>

          <Box flexDir="row" gap="medium" justify="start" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              Sic Code:
            </TitleComponent>
            <EditableWrapper
              placeholder="Write something ..."
              value={companyJson?.industry?.sic_code ?? ""}
              onChange={(val) => handleChange("industry.sic_code", val)}
              maxLines={1}
              className={`${
                darkMode ? "muted-text" : "muted-text-opacity-1"
              } editable-text-sm-max-width`}
            />
          </Box>
        </FlexColumn>
        <FlexColumn>
          <Box flexDir="row" items="center" justify="start" gap="small">
            <TitleComponent fontSize="15px" marginTop="0px" marginBottom="0px">
              Incorporation Date:
            </TitleComponent>
            <Text muted>
              {moment(companyJson?.incorporation_date ?? "").format(
                "DD/MM/YYYY"
              )}
            </Text>
          </Box>

          <Box flexDir="row" items="center" justify="start" gap="small">
            <TitleComponent fontSize="15px" marginTop="0px" marginBottom="0px">
              Company Type:
            </TitleComponent>
            <EditableWrapper
              placeholder="Write something ..."
              value={companyJson?.company_type ?? ""}
              onChange={(val) => handleChange("company_type", val)}
              maxLines={1}
              className={`${darkMode ? "muted-text" : "muted-text-opacity-1"}`}
            />
          </Box>
          <Box flexDir="row" gap="medium" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              LinkedIn:
            </TitleComponent>
            <Box flexDir="row" items="center" gap={"xsmall"} width={"2/3"}>
              <EditableWrapper
                placeholder="Write something ..."
                value={companyJson?.url?.linkedin ?? ""}
                onChange={(val) => handleChange("url.linkedin", val)}
                maxLines={1}
                className={`${
                  darkMode ? "muted-text" : "muted-text-opacity-1"
                }`}
              />
              <Tooltip2 content="Open LinkedIn Url">
                <Icon
                  className="cursor-pointer  mb-xs"
                  color="#ABB3BF"
                  icon="share"
                  iconSize={13}
                  onClick={() => openInNewTab(companyJson?.url?.linkedin || "")}
                />
              </Tooltip2>
            </Box>
          </Box>
          <Box flexDir="row" gap="medium" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              Lsic Code:
            </TitleComponent>
            <EditableWrapper
              placeholder="Write something ..."
              value={companyJson?.industry?.lsic_code ?? ""}
              onChange={(val) => handleChange("industry.lsic_code", val)}
              maxLines={1}
              className={`editable-text-sm-max-width ${
                darkMode ? "muted-text" : "muted-text-opacity-1"
              }`}
            />
          </Box>
        </FlexColumn>
        <FlexColumn>
          <Box flexDir="row" items="center" justify="start" gap="small">
            <TitleComponent fontSize="15px" marginTop="0px" marginBottom="0px">
              Status:
            </TitleComponent>
            <Text muted>{companyJson?.status ?? ""}</Text>
          </Box>

          <Box flexDir="row" gap="medium" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              Logo:
            </TitleComponent>
            <Box flexDir="row" items="center" gap={"xsmall"} width={"2/3"}>
              <EditableWrapper
                placeholder="Write something ..."
                value={companyJson?.url?.logo ?? ""}
                onChange={(val) => handleChange("url.logo", val)}
                maxLines={1}
                className={`${
                  darkMode ? "muted-text" : "muted-text-opacity-1"
                }`}
              />
              <Button
                icon="eye-open"
                small
                minimal
                onClick={() => {
                  setEyeOpen(!eyeOpen);
                }}
              />
            </Box>
            {eyeOpen && (
              <ThemedDialog
                title="Logo Preview"
                icon="info-sign"
                isOpen={eyeOpen}
                onClose={() => setEyeOpen(false)}
              >
                <DialogBody className="display-flex align-center justify-center overflow-hidden">
                  <StyledImage
                    src={companyJson?.url?.logo ?? ""}
                    alt="Company Logo"
                    className="p-sm"
                  />
                </DialogBody>
              </ThemedDialog>
            )}
          </Box>
          <Box flexDir="row" gap="medium" items="center">
            <TitleComponent fontSize="15px" marginTop="0px">
              Twitter:
            </TitleComponent>
            <Box flexDir="row" items="center" gap={"xsmall"} width={"2/3"}>
              <EditableWrapper
                placeholder="Write something ..."
                value={companyJson?.url?.twitter ?? ""}
                onChange={(val) => handleChange("url.twitter", val)}
                maxLines={1}
                className={`${
                  darkMode ? "muted-text" : "muted-text-opacity-1"
                }`}
              />
              <Tooltip2 content="Open twitter Url">
                <Icon
                  className="cursor-pointer mb-xs"
                  color="#ABB3BF"
                  icon="share"
                  iconSize={13}
                  onClick={() => openInNewTab(companyJson?.url?.twitter || "")}
                />
              </Tooltip2>
            </Box>
          </Box>
        </FlexColumn>
      </Box>
      <CustomBox mt="small" py={"large"} flexDir="col" gap={"medium"}>
        <Box flexDir="row" justify="between" width={"1"} gap={"large"}>
          <Box width={"1/2"}>
            <TagsMultiSelect
              values={tagValues}
              setValues={setTagValues}
              changeFormData={handleChange}
            />
          </Box>
          <Box width={"1/2"}>
            <BadgesMultiSelect
              values={badgeValues}
              setValues={setBadgeValues}
              changeFormData={handleChange}
            />
          </Box>
        </Box>
        <Box flexDir="row" justify="between" width={"1"} gap={"large"}>
          <Box width={"1/2"} flexDir="row" gap={"medium"}>
            <TitleComponent fontSize="15px" marginTop="0px" marginBottom="8px">
              Industry:
            </TitleComponent>
            <CustomSelect
              flexDir="row"
              isActiveItem={true}
              label={industryCurrentObj ? industryCurrentObj.label : "Select"}
              items={INDUSTRY_OPTIONS_OBJ}
              values={[industryCurrentObj ? industryCurrentObj.value : ""]}
              onSelect={(item) => handleChange("industry.industry", item.value)}
              filterable={INDUSTRY_OPTIONS_OBJ.length > 15}
            />
          </Box>
          <Box
            width={"1/2"}
            flexDir="row"
            gap={"medium"}
            height={"large"}
            items="center"
          >
            <TitleComponent fontSize="15px" marginTop="0px">
              SubIndustry:
            </TitleComponent>
            <EditableWrapper
              placeholder="Write something ..."
              value={companyJson?.industry?.sub_industry ?? ""}
              onChange={(val) => handleChange("industry.sub_industry", val)}
              maxLines={1}
              className={`${darkMode ? "muted-text" : "muted-text-opacity-1"}`}
            />
          </Box>
        </Box>
        <Box flexDir="row" justify="between" width={"1"} gap={"large"}>
          <Box width={"1/2"}>
            <Categories
              values={categoryValues}
              setValues={setCategoryValues}
              changeFormData={handleChange}
            />
          </Box>
          <Box width={"1/2"} flexDir="row" gap={"medium"}>
            <TitleComponent fontSize="15px" marginTop="0px">
              Market Capital:
            </TitleComponent>
            <CustomSelect
              flexDir="row"
              isActiveItem={true}
              label={marketCurrentObj ? marketCurrentObj.label : "Select"}
              items={MARKET_CAP_OPTIONS_OBJ}
              values={[marketCurrentObj ? marketCurrentObj.value : ""]}
              onSelect={(item) =>
                handleChange("industry.market_cap", item.value)
              }
            />
          </Box>
        </Box>
      </CustomBox>
      <Box flexDir="row" gap="medium" items="center" mt={"small"}>
        <TitleComponent fontSize="15px" marginTop="0px">
          Description:
        </TitleComponent>
        <EditableText
          placeholder="Write something ..."
          value={companyJson?.description ?? ""}
          onChange={(val) => handleChange("description", val)}
          maxLines={1}
          className={`${darkMode ? "muted-text" : "muted-text-opacity-1"} w-800 overflow-hidden`}
        />
      </Box>
    </Box>
  );
};

export default CompanyDetailDisplay;

const EditableWrapper = styled(EditableText)`
  flex: 1;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FlexColumn = styled(Box).attrs({
  flexDir: "col",
  gap: "medium",
  width: "1/3",
})`
  min-width: 0;
`;
const CustomBox = styled(Box)`
  border-top: ${({ theme: { darkMode } }) =>
    `1px solid ${darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1}`};
  border-bottom: ${({ theme: { darkMode } }) =>
    `1px solid ${darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1}`};
`;
const StyledImage = styled.img`
  max-height: 100%;
  max-width: 100%;
`;
